<template>
  <div :class="InputContainerClass">
    <!-- Label -->
    <label v-show="label" :for="name" class="font-body text-base text-gray-900 block" :class="labelCustomCss">
      {{ label }}
      <span v-show="rules == 'required' || rules.required == true" class="text-orange ml-1">*</span>
    </label>

    <!-- Select -->
    <div class="relative" :class="selectWrapperClass || { 'mt-2': label }">
      <select
        ref="selectRef"
        v-validate="rules"
        :name="name"
        :value="value"
        :disabled="disabled"
        :data-vv-as="label || placeholder"
        @input="$emit('input', coerceValue($event.target.value))"
        @mousedown="selectMousePress"
        @click="selectClickHandle"
        @blur="selectBlurHandle"
        @keydown="selectKeyHandle"
        :class="[{ 'input-error': errors.has(name), 'select-placeholder': value === '' }, selectModeClass]"
        v-model="selectedValue"
        @change="
          $emit('input', coerceValue(selectedValue))
          $emit('change', selectedValue, $event)
        "
      >
        <option value="" disabled hidden>{{ placeholder }}</option>
        <option v-if="normalizedOptions.length === 0" disabled>{{ $t('no_records').tTitle() }}</option>
        <option v-else v-for="(option, key) in normalizedOptions" :key="key" :value="option.value" :data-currency="option.currency" :ref="option.optionsRef">
          {{ getLabel(option) }}
        </option>
      </select>

      <!--  Arrow -->
      <img
        :class="{ 'rotate-180': isOpen }"
        class="absolute top-[18px] h-[13px] ltr:right-3 rtl:left-3 transform transition-transform duration-200 pointer-events-none"
        width="13px"
        :src="errors.has(name) ? require('../../assets/img/dropdown-arrow-red.svg') : require('../../assets/img/icons/upload/arrow-down.svg')"
        alt="arrow"
      />
    </div>

    <!-- Error -->
    <span v-show="errors.has(name)" class="font-body text-xs text-orange font-semibold mt-1 block">
      {{ errors.first(name) }}
    </span>
  </div>
</template>

<script>
export default {
  name: 'Select',
  inject: ['$validator'],

  props: {
    /**
     * The currently selected value of the select field (used with v-model).
     * It must match one of the values in the options list.
     */
    value: [String, Number],

    /**
     * The list of options to render in the dropdown.
     * - Accepts an array of objects: [{ value: 'x', label: 'X' }]
     * - Or an object of objects: { x: { label: 'X' }, y: { label: 'Y' } }
     */
    options: {
      type: [Array, Object],
      required: true,
    },

    /**
     * Unique name used to identify the field for validation and error tracking.
     * This should match the key used in the `v-model` or form object.
     */
    name: {
      type: String,
      required: true,
    },

    /**
     * Validation rules passed to VeeValidate (e.g., 'required', 'required|numeric').
     * Accepts a string or an object.
     */
    rules: {
      type: [String, Object],
      default: '',
    },

    /**
     * Placeholder text displayed when no option is selected.
     * Only shown if the current value is empty.
     */
    placeholder: {
      type: String,
      default: 'Select an option',
    },

    /**
     * Label text shown above the select field (e.g., "Choose currency").
     * Should be a string or a translation key (used with $t).
     */
    label: {
      type: String,
      default: '',
    },

    /**
     * Custom css applied to label of the field. It is expected to be string
     */
    labelCustomCss: {
      type: Object,
      default: () => ({})
    },

    /**
     * The key in each option object that should be used as the actual value.
     * Defaults to 'value', but can be customized if options use a different structure.
     */
    optionValue: {
      type: String,
      default: 'value',
    },

    /**
     * The key in each option object that should be used as the display label.
     * Defaults to 'label', but can be customized to use other keys or translated labels.
     */
    optionLabel: {
      type: String,
      default: 'label',
    },

    /**
     * Optional key in each option to extract currency data (if needed for contextual display).
     * Defaults to 'currency'.
     */
    optionCurrency: {
      type: String,
      default: 'currency',
    },

    /**
     * Whether the select dropdown should be disabled.
     * Disables interaction and dims the field.
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * A prefix string (e.g., "+", "≈") to show before each option's label.
     * Can be used for symbols or contextual info.
     */
    prefix: {
      type: String,
      default: '',
    },

    /**
     * Whether to actually render the prefix before each option label.
     * Useful for toggling dynamic label decoration.
     */
    usePrefix: {
      type: Boolean,
      default: false,
    },

    /**
     * Name of a string method to apply on the label text (e.g., 'tTitle', 'toUpperCase').
     * This method will be called on the label string inside the component.
     */
    labelTransform: {
      type: String,
      default: 'tTitle',
    },

    /**
     * If true, automatically converts selected value to a number.
     * Useful when working with numeric form fields or filters.
     */
    coerceToNumber: {
      type: Boolean,
      default: false,
    },

    /**
     * A suffix to append to each option label.
     * Can be a string (e.g., a unit or symbol) or a dynamic field name (e.g., 'currency').
     */
    suffix: {
      type: [String, Function],
      default: '',
    },

    /**
     * Custom CSS class to apply to the outer container of the select field.
     * Useful for controlling layout or column widths in forms.
     */
    InputContainerClass: {
      type: String,
      default: 'w-full',
    },

    /**
     * Custom CSS class for the select wrapper element.
     * When omitted, `mt-2` is applied when a label is present.
     */
    selectWrapperClass: {
      type: [String, Object, Array],
      default: '',
    },

    /**
     * Styling mode for the select field appearance.
     * Can be extended in the future.
     */
    mode: {
      type: String,
      default: 'primary',
    },
  },

  data() {
    return {
      isOpen: false,
      selectedValue: this.value,
    }
  },
  watch: {
    value(newVal) {
      this.selectedValue = newVal
    },
  },
  computed: {
    normalizedOptions() {
      if (Array.isArray(this.options)) {
        return this.options.map(opt => ({
          ...opt,
          value: opt[this.optionValue] ?? opt,
          label: opt[this.optionLabel] ?? opt,
          currency: opt[this.optionCurrency] ?? null,
        }))
      } else if (typeof this.options === 'object') {
        return Object.entries(this.options).map(([key, opt]) => ({
          ...opt,
          value: opt[this.optionValue] ?? key,
          label: opt[this.optionLabel] ?? key,
          currency: opt[this.optionCurrency] ?? null,
        }))
      }
      return []
    },
    selectModeClass() {
      const modeStyles = {
        primary: 'border-none bg-gray-100 ',
        primaryWhiteOutline: 'border border-gray-300 text-blue bg-white border-solid focus:border-blue focus:ring-0 focus:outline-none focus:ring-offset-0',
        primaryWhite: 'border-none  bg-white focus:ring-0 focus:outline-none focus:ring-offset-0',
        secondary: 'border border-gray-300  text-blue  bg-white border-solid  focus:border-gray-300 focus:ring-0 focus:outline-none focus:ring-offset-0',
      }
      return modeStyles[this.mode] || modeStyles.default
    },
  },
  methods: {
    selectClickHandle(event) {
      const component = this.$refs.selectRef
      if (component) {
        const rect = component.getBoundingClientRect()
        const { clientX, clientY } = event
        const isInComponent = clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
        if (!isInComponent) {
          this.isOpen = false
        }
      }
    },
    selectBlurHandle() {
      this.isOpen = false
    },
    selectKeyHandle(event) {
      if (event.key === 'Enter' || event.keyCode === 13 || event.key === ' ' || event.keyCode === 32) {
        this.isOpen = true
      }
    },
    selectMousePress() {
      this.isOpen = !this.isOpen
    },
    applyTransform(label) {
      if (typeof label !== 'string') return label

      // If transform exists globally on string, apply it
      const transformFn = label[this.labelTransform]
      if (typeof transformFn === 'function') {
        return transformFn.call(label)
      }

      return label
    },
    coerceValue(val) {
      return this.coerceToNumber ? Number(val) : val
    },
    getLabel(option) {
      let label = option.label

      // If label is a translation key, translate it
      if (typeof label === 'string' && this.$te(label)) {
        label = this.$t(label)
      }

      // Apply transform if needed
      label = this.applyTransform(label)

      const prefixPart = this.usePrefix && this.prefix ? this.prefix : ''
      let suffixPart = ''
      if (typeof this.suffix === 'string' && this.suffix !== '') {
        const suffixValue = option[this.suffix]
        suffixPart = suffixValue ? `(${suffixValue})` : `${this.suffix}`
      }

      return `${prefixPart}${label}${suffixPart}`
    },
  },
}
</script>
